<template>
  <v-row>
    <v-col cols="12">
      <!-- Header -->
      <v-row justify="space-between" align="center">
        <v-col cols="auto">
          <h1>Логи приложения</h1>
        </v-col>
      </v-row>
    </v-col>

    <!-- Stats Cards -->
    <v-col cols="12">
      <v-skeleton-loader v-if="statsLoading" type="chip@6" />

      <div v-else class="d-flex align-center flex-wrap" style="gap: 8px;">
        <v-chip small outlined>
          Всего: <strong class="ml-1">{{ totalCount }}</strong>
        </v-chip>

        <v-chip small outlined color="error">
          Ошибки: <strong class="ml-1">{{ errorCount }}</strong>
        </v-chip>

        <v-chip
            v-for="(sys, i) in systemStats"
            :key="'sys-' + i"
            small
            :color="systemColor(sys.system)"
            dark
        >
          {{ sys.system }}: {{ sys.count }}
        </v-chip>
      </div>
    </v-col>

    <!-- Filters -->
    <v-col cols="12">
      <v-card elevation="0">
        <v-card-text>
          <v-row align="center">
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                  v-model="filters.search"
                  prepend-inner-icon="mdi-magnify"
                  label="Поиск по содержимому"
                  outlined
                  dense
                  hide-details
                  clearable
                  @input="onFilterChange"
              />
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-select
                  v-model="filters.system"
                  :items="systemOptions"
                  label="Система"
                  outlined
                  dense
                  hide-details
                  clearable
                  @change="onFilterChange"
              />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-select
                  v-model="filters.type"
                  :items="typeOptions"
                  label="Тип"
                  outlined
                  dense
                  hide-details
                  clearable
                  @change="onFilterChange"
              />
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-select
                  v-model="filters.is_error"
                  :items="errorOptions"
                  label="Статус"
                  outlined
                  dense
                  hide-details
                  clearable
                  @change="onFilterChange"
              />
            </v-col>
            <v-col cols="12" sm="auto">
              <v-btn text @click="resetFilters">
                <v-icon left>mdi-filter-remove</v-icon>
                Сбросить
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-col>

    <!-- Table -->
    <v-col cols="12">
      <v-data-table
          :headers="headers"
          :items="items"
          :loading="loading"
          :options.sync="options"
          :server-items-length="totalItems"
          :footer-props="footerProps"
          :header-props="{ sortIcon: 'icon-custom-arrow' }"
          no-data-text="Логи не найдены"
          no-results-text="По заданному запросу ничего не найдено"
          loading-text="Загрузка... Подождите пожалуйста"
          @click:row="openLogDialog"
          class="app-logs-table"
      >
        <!-- System Column -->
        <template #item.system="{ item }">
          <v-chip
              small
              :color="systemColor(item.system)"
              dark
          >
            {{ item.system }}
          </v-chip>
        </template>

        <!-- Type Column -->
        <template #item.type="{ item }">
          <code class="log-type">{{ item.type }}</code>
        </template>

        <!-- Subject Column -->
        <template #item.subject="{ item }">
          <span class="text-truncate d-inline-block" style="max-width: 250px;">
            {{ item.subject || '—' }}
          </span>
        </template>

        <!-- Error Column -->
        <template #item.is_error="{ item }">
          <v-icon :color="item.is_error ? 'error' : 'success'" small>
            {{ item.is_error ? 'mdi-alert-circle' : 'mdi-check-circle' }}
          </v-icon>
        </template>

        <!-- Created At Column -->
        <template #item.created_at="{ item }">
          <span class="text-caption">{{ formatDate(item.created_at) }}</span>
        </template>

        <!-- Actions Column -->
        <template #item.actions="{ item }">
          <div class="text-right" @click.stop>
            <v-btn icon small @click="openLogDialog(item)" title="Просмотр">
              <v-icon small>mdi-eye</v-icon>
            </v-btn>
            <v-btn icon small color="error" @click="openDeleteDialog(item)" title="Удалить">
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-col>

    <!-- Log Detail Dialog -->
    <v-dialog v-model="logDialog.show" max-width="800" scrollable>
      <v-card v-if="logDialog.item">
        <v-card-title class="d-flex justify-space-between align-center">
          <div class="d-flex align-center gap-2" style="gap: 8px;">
            <v-chip small :color="systemColor(logDialog.item.system)" dark>
              {{ logDialog.item.system }}
            </v-chip>
            <code>{{ logDialog.item.type }}</code>
          </div>
          <v-btn icon @click="logDialog.show = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text class="pt-4">
          <!-- Meta info -->
          <v-row dense class="mb-3">
            <v-col cols="12" sm="6">
              <div class="text-caption grey--text">ID</div>
              <div class="font-weight-medium">#{{ logDialog.item.id }}</div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="text-caption grey--text">Статус</div>
              <v-chip x-small :color="logDialog.item.is_error ? 'error' : 'success'" dark>
                {{ logDialog.item.is_error ? 'Ошибка' : 'Успешно' }}
              </v-chip>
            </v-col>
            <v-col cols="12" sm="6" v-if="logDialog.item.subject">
              <div class="text-caption grey--text">Тема</div>
              <div class="font-weight-medium">{{ logDialog.item.subject }}</div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="text-caption grey--text">Дата создания</div>
              <div class="font-weight-medium">{{ formatDate(logDialog.item.created_at) }}</div>
            </v-col>
            <v-col cols="12" sm="6" v-if="logDialog.item.user_id">
              <div class="text-caption grey--text">User ID</div>
              <div class="font-weight-medium">{{ logDialog.item.user_id }}</div>
            </v-col>
            <v-col cols="12" sm="6" v-if="logDialog.item.version">
              <div class="text-caption grey--text">Версия</div>
              <div class="font-weight-medium">{{ logDialog.item.version }}</div>
            </v-col>
            <v-col cols="12" v-if="logDialog.item.url">
              <div class="text-caption grey--text">URL</div>
              <div class="font-weight-medium text-break">{{ logDialog.item.url }}</div>
            </v-col>
          </v-row>

          <v-divider class="mb-4" />

          <!-- iiko order status check -->
          <template v-if="logDialog.item.type === 'iiko_order' && iikoOrderId">
            <div class="d-flex align-center justify-space-between mb-2">
              <div class="text-subtitle-2">
                Статус заказа iiko
                <code class="log-type ml-2">{{ iikoOrderId }}</code>
              </div>
              <v-btn
                  text
                  x-small
                  color="primary"
                  :loading="orderStatus.loading"
                  @click="checkOrderStatus"
              >
                <v-icon x-small left>mdi-refresh</v-icon>
                Проверить статус
              </v-btn>
            </div>

            <v-sheet
                v-if="orderStatus.result !== null"
                :color="orderStatus.error ? 'red lighten-5' : 'green lighten-5'"
                rounded
                class="pa-3 mb-4"
            >
              <pre class="log-body text-caption">{{ orderStatus.result }}</pre>
            </v-sheet>

            <v-divider class="mb-4" />
          </template>

          <template v-if="glovoTransactionId">
            <div class="d-flex align-center justify-space-between mb-2">
              <div class="text-subtitle-2">
                Статус транзакции Glovo
                <code class="log-type ml-2">{{ glovoTransactionId }}</code>
              </div>
              <v-btn
                  text
                  x-small
                  color="primary"
                  :loading="glovoTransaction.loading"
                  @click="checkGlovoTransactionStatus"
              >
                <v-icon x-small left>mdi-refresh</v-icon>
                Проверить статус
              </v-btn>
            </div>

            <v-sheet
                v-if="glovoTransaction.result !== null"
                :color="glovoTransaction.error ? 'red lighten-5' : 'green lighten-5'"
                rounded
                class="pa-3 mb-4"
            >
              <pre class="log-body text-caption">{{ glovoTransaction.result }}</pre>
            </v-sheet>

            <v-divider class="mb-4" />
          </template>

          <!-- Body -->
          <div class="d-flex justify-space-between align-center mb-2">
            <div class="text-subtitle-2">Содержимое (body)</div>
            <v-btn
                text
                x-small
                color="primary"
                @click="copyBody"
            >
              <v-icon x-small left>mdi-content-copy</v-icon>
              Скопировать
            </v-btn>
          </div>
          <v-sheet
              color="grey lighten-4"
              rounded
              class="pa-3"
              style="max-height: 400px; overflow-y: auto;"
          >
            <pre class="log-body text-caption">{{ formattedBody }}</pre>
          </v-sheet>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-btn
              text
              color="error"
              @click="openDeleteFromDialog"
          >
            <v-icon left small>mdi-delete</v-icon>
            Удалить
          </v-btn>
          <v-spacer />
          <v-btn text @click="logDialog.show = false">Закрыть</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog.show" max-width="400">
      <v-card>
        <v-card-title>Подтвердить удаление</v-card-title>
        <v-card-text>
          Вы уверены, что хотите удалить лог <strong>#{{ deleteDialog.item && deleteDialog.item.id }}</strong>?
          Это действие необратимо.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteDialog.show = false">Отмена</v-btn>
          <v-btn color="error" text :loading="deleteDialog.loading" @click="handleDelete">Удалить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        :timeout="3000"
        top
        right
    >
      {{ snackbar.message }}
      <template #action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Закрыть</v-btn>
      </template>
    </v-snackbar>
  </v-row>
</template>

<script>
import AppLogsService from '@/api/AppLogService'

export default {
  name: 'AppLogsIndex',

  data() {
    return {
      // Table data
      items: [],
      totalItems: 0,
      loading: false,

      // Stats
      stats: [],
      statsLoading: false,

      // Filters
      filters: {
        search: '',
        system: null,
        type: null,
        is_error: null,
      },

      filterTimeout: null,

      // Table options (pagination + sort)
      options: {
        page: 1,
        itemsPerPage: 50,
      },

      footerProps: {
        itemsPerPageText: 'Элементов на страницу',
        pageText: '{0}-{1} из {2}',
        itemsPerPageOptions: [25, 50, 100],
      },

      headers: [
        { text: 'ID', value: 'id', width: '70px', sortable: false },
        { text: 'Система', value: 'system', width: '100px', sortable: false },
        { text: 'Тип', value: 'type', sortable: false },
        { text: 'Тема', value: 'subject', sortable: false },
        { text: 'Ошибка', value: 'is_error', width: '80px', sortable: false, align: 'center' },
        { text: 'Дата', value: 'created_at', width: '160px', sortable: false },
        { text: '', value: 'actions', width: '90px', sortable: false },
      ],

      systemOptions: [
        { text: 'Glovo', value: 'glovo' },
        { text: 'iiko', value: 'iiko' },
        { text: 'Order', value: 'order' },
        { text: 'Checkbox', value: 'checkbox' },
        { text: 'App', value: 'app' },
      ],

      typeOptions: [
        { text: 'glovo_export', value: 'glovo_export' },
        { text: 'glovo_update', value: 'glovo_update' },
        { text: 'glovo_stop', value: 'glovo_stop' },
        { text: 'glovo_order', value: 'glovo_order' },
        { text: 'receipt_exception', value: 'receipt_exception' },
        { text: 'iiko_order', value: 'iiko_order' },
        { text: 'order_error', value: 'order_error' },
        { text: 'system', value: 'system' },
      ],

      errorOptions: [
        { text: 'Все', value: null },
        { text: 'Только ошибки', value: true },
        { text: 'Без ошибок', value: false },
      ],

      // Log detail dialog
      logDialog: {
        show: false,
        item: null,
      },

      // iiko order status
      orderStatus: {
        loading: false,
        result: null,
        error: false,
      },

      // glovo transaction status
      glovoTransaction: {
        loading: false,
        result: null,
        error: false,
      },

      // Delete dialog
      deleteDialog: {
        show: false,
        item: null,
        loading: false,
      },

      // Snackbar
      snackbar: {
        show: false,
        message: '',
        color: 'success',
      },
    }
  },

    computed: {
    totalCount() {
      return this.stats.reduce((sum, s) => sum + s.count, 0)
    },

    errorCount() {
      return this.stats
          .filter(s => s.is_error)
          .reduce((sum, s) => sum + s.count, 0)
    },

    systemStats() {
      const map = {}
      for (const s of this.stats) {
        if (!map[s.system]) map[s.system] = { system: s.system, count: 0 }
        map[s.system].count += s.count
      }

      return Object.values(map).sort((a, b) => b.count - a.count)
    },

    formattedBody() {
      if (!this.logDialog.item) return ''
      try {
        return JSON.stringify(JSON.parse(this.logDialog.item.body), null, 2)
      } catch {
        return this.logDialog.item.body || ''
      }
    },

    iikoOrderId() {
      if (!this.logDialog.item || this.logDialog.item.type !== 'iiko_order') return null
      try {
        const parsed = JSON.parse(this.logDialog.item.body)
        return parsed?.response?.orderInfo?.id || null
      } catch {
        return null
      }
    },

    glovoTransactionId() {
      if (!this.logDialog.item || !['glovo_update', 'glovo_export'].includes(this.logDialog.item.type)) return null
      const match = this.logDialog.item.subject?.match(/queue_transaction_id:\s*(\S+)/)
      return match ? match[1] : null
    },

    glovoStoreId() {
      const tid = this.glovoTransactionId
      if (!tid) return null
      const match = tid.match(/^(\d+)-/)
      return match ? match[1] : null
    },
  },

  watch: {
    options: {
      handler() {
        this.fetchData()
      },
      deep: true,
    },
  },

  mounted() {
    this.fetchStats()
    this.fetchData()
  },

  methods: {
    // ---- Data Fetching ----
    async fetchData() {
      this.loading = true
      try {
        const { page, itemsPerPage } = this.options
        const params = {
          page,
          per_page: itemsPerPage,
          ...(this.filters.search && { search: this.filters.search }),
          ...(this.filters.system && { system: this.filters.system }),
          ...(this.filters.type && { type: this.filters.type }),
          ...(this.filters.is_error !== null && this.filters.is_error !== undefined && { is_error: this.filters.is_error }),
        }
        const response = await AppLogsService.list(params)
        const data = response.data
        this.items = data.data
        this.totalItems = data.total
      } catch (error) {
        this.showError('Ошибка при загрузке логов')
        console.error(error)
      } finally {
        this.loading = false
      }
    },

    async fetchStats() {
      this.statsLoading = true
      try {
        const response = await AppLogsService.stats()
        this.stats = response.data
      } catch (error) {
        console.error('Ошибка при загрузке статистики', error)
      } finally {
        this.statsLoading = false
      }
    },

    // ---- Filters ----
    onFilterChange() {
      clearTimeout(this.filterTimeout)
      this.filterTimeout = setTimeout(() => {
        this.options = { ...this.options, page: 1 }
        this.fetchData()
      }, 400)
    },

    resetFilters() {
      this.filters = { search: '', system: null, type: null, is_error: null }
      this.options = { ...this.options, page: 1 }
      this.fetchData()
    },

    // ---- Log Dialog ----
    openLogDialog(item) {
      this.logDialog.item = item
      this.logDialog.show = true
      this.orderStatus = { loading: false, result: null, error: false }
      this.glovoTransaction = { loading: false, result: null, error: false }
    },

    async checkOrderStatus() {
      if (!this.iikoOrderId) return
      this.orderStatus = { loading: true, result: null, error: false }
      try {
        const response = await AppLogsService.orderStatus(this.iikoOrderId)
        this.orderStatus.result = JSON.stringify(response.data, null, 2)
        this.orderStatus.error = false
      } catch (error) {
        const errData = error?.response?.data
        this.orderStatus.result = errData
            ? JSON.stringify(errData, null, 2)
            : (error.message || 'Ошибка запроса')
        this.orderStatus.error = true
      } finally {
        this.orderStatus.loading = false
      }
    },

    async checkGlovoTransactionStatus() {
      const transactionId = this.glovoTransactionId
      const storeId = this.glovoStoreId
      if (!transactionId || !storeId) return
      this.glovoTransaction = { loading: true, result: null, error: false }
      try {
        const response = await AppLogsService.glovoTransactionStatus(storeId, transactionId)
        this.glovoTransaction.result = JSON.stringify(response.data, null, 2)
        this.glovoTransaction.error = false
      } catch (error) {
        const errData = error?.response?.data
        this.glovoTransaction.result = errData
            ? JSON.stringify(errData, null, 2)
            : (error.message || 'Ошибка запроса')
        this.glovoTransaction.error = true
      } finally {
        this.glovoTransaction.loading = false
      }
    },

    async copyBody() {
      try {
        await navigator.clipboard.writeText(this.formattedBody)
        this.showSuccess('Скопировано в буфер обмена')
      } catch {
        this.showError('Не удалось скопировать')
      }
    },

    // ---- Delete ----
    openDeleteDialog(item) {
      this.deleteDialog.item = item
      this.deleteDialog.show = true
    },

    openDeleteFromDialog() {
      this.logDialog.show = false
      this.openDeleteDialog(this.logDialog.item)
    },

    async handleDelete() {
      if (!this.deleteDialog.item) return
      this.deleteDialog.loading = true
      try {
        await AppLogsService.remove(this.deleteDialog.item.id)
        this.showSuccess('Лог успешно удалён')
        this.deleteDialog.show = false
        this.deleteDialog.item = null
        await this.fetchData()
        await this.fetchStats()
      } catch (error) {
        this.showError('Ошибка при удалении лога')
        console.error(error)
      } finally {
        this.deleteDialog.loading = false
      }
    },

    // ---- Helpers ----
    systemColor(system) {
      const colors = {
        glovo: 'orange darken-2',
        iiko: 'blue darken-1',
        order: 'purple',
        checkbox: 'teal',
      }
      return colors[system] || 'grey'
    },

    formatDate(dateStr) {
      if (!dateStr) return '—'
      const d = new Date(dateStr)
      return d.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    },

    showSuccess(message) {
      this.snackbar = { show: true, message, color: 'success' }
    },

    showError(message) {
      this.snackbar = { show: true, message, color: 'error' }
    },
  },
}
</script>

<style lang="scss" scoped>
.app-logs-table {
  ::v-deep tbody tr {
    cursor: pointer;

    &:hover {
      background-color: rgba(0, 0, 0, 0.04) !important;
    }
  }
}

.log-type {
  background: rgba(0, 0, 0, 0.06);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
}

.log-body {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-all;
  font-family: 'Fira Code', 'Courier New', monospace;
  font-size: 12px;
  line-height: 1.6;
}
</style>